// wordMatch — Fundstellen-Heuristik: ordnet extrahierte Feldwerte den OCR-Wort-Boxen im PDF zu.
// Direkter Port der OC-Pilot-Heuristik (frontend/src/pages/DocumentDetail.tsx, Zeilen ~97-205,
// siehe BAUPLAN-AB-UI-PARITAET Agent FE-2) auf die AB-Modul-Feldnamen (lowercase: ebeln/ebelp/
// labnr/matnr/mfrpn/menge/netpr/eeind/waers statt EBELN/EBELP/...).
//
// Es gibt KEINE gespeicherte Herkunft (die Regel-Engine liefert keine source_bbox) — der
// (normalisierte) Feldwert wird best-effort gegen die Wortboxen aus GET /documents/:id/words
// gematcht: feldtyp-bewusst (IDs/Text substring-tolerant, Menge/Preis numerisch de/us-Format,
// Liefertermin als Datum in mehreren Schreibweisen). Trifft eindeutige Werte (IDs, Material-/
// HTN-Nr, Datum, Waehrung) praezise; mehrdeutige Kurzzahlen (z. B. Menge) koennen mehrfach markiert
// werden — bewusst in Kauf genommen (informativer Hinweis, kein Beweis).

const NUMERIC_FIELDS = new Set(['menge', 'netpr']);
const DATE_FIELDS = new Set(['eeind']);

// Felder, die als Quellwert fuer Fundstellen infrage kommen (Reihenfolge wie AbLineTable-Spalten).
// 'einteilung' ist ein manueller Kommentar OHNE Quellwert im PDF -> bewusst ausgeschlossen.
const SOURCE_FIELDS = ['ebeln', 'ebelp', 'labnr', 'matnr', 'mfrpn', 'menge', 'netpr', 'eeind', 'waers'];

/** Vergleichsnormalform fuer IDs/Text: Kleinbuchstaben, Trenner/Whitespace entfernt
 * (so matcht z. B. 'ORD-0000437202-1' <-> 'ord00004372021'). */
const normId = (s) => String(s ?? '').toLowerCase().replace(/[\s./_,-]/g, '');

/** Zahl aus Text lesen (dt. ODER us-Format; Einheiten/Waehrungszeichen werden ignoriert). */
function parseNum(s) {
  const m = String(s ?? '').replace(/[^\d.,-]/g, '');
  if (!/\d/.test(m)) return null;
  let t = m;
  const c = t.lastIndexOf(',');
  const d = t.lastIndexOf('.');
  if (c >= 0 && d >= 0) t = c > d ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  else if (c >= 0) t = t.replace(',', '.');
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const approxEq = (a, b) => Math.abs(a - b) <= Math.max(0.0005, Math.abs(b) * 1e-4);

/** eeind ist als ISO (YYYY-MM-DD) gespeichert -> gaengige Druckformate als Suchkandidaten erzeugen. */
function dateCandidates(v) {
  let iso = String(v ?? '').trim();
  const dmy = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/.exec(iso);
  if (dmy) iso = `${dmy[3].length <= 2 ? '20' + dmy[3] : dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return [];
  const [, y, mo, da] = m;
  const out = new Set();
  for (const sep of ['.', '/', '-']) {
    out.add(`${da}${sep}${mo}${sep}${y}`); // 24.09.2026
    out.add(`${+da}${sep}${+mo}${sep}${y}`); // 24.9.2026
    out.add(`${da}${sep}${mo}${sep}${y.slice(2)}`); // 24.09.26
  }
  out.add(`${y}-${mo}-${da}`);
  return [...out];
}

/** Konsekutive Woerter einer PDF-Zeile (gleiche gerundete y0) zusammenfuegen und den Wert
 * als Teilstring suchen (z. B. mehrteilige Material-/HTN-Bezeichnung ueber mehrere Tokens). */
function matchMultiWord(value, pages) {
  const target = normId(value);
  if (target.length < 4) return [];
  const hits = [];
  for (const pg of pages) {
    const rows = new Map();
    for (const w of pg.words || []) {
      const k = Math.round(w.y0);
      const arr = rows.get(k);
      if (arr) arr.push(w); else rows.set(k, [w]);
    }
    for (const row of rows.values()) {
      const sorted = [...row].sort((a, b) => a.x0 - b.x0);
      let text = '';
      const spans = [];
      for (const w of sorted) {
        const s = text.length;
        text += normId(w.t);
        spans.push({ s, e: text.length, w });
      }
      for (let idx = text.indexOf(target); idx >= 0; idx = text.indexOf(target, idx + 1)) {
        const cover = spans.filter((sp) => sp.s < idx + target.length && sp.e > idx);
        if (!cover.length) continue;
        hits.push({
          page: pg.page,
          box: {
            x0: Math.min(...cover.map((c) => c.w.x0)),
            y0: Math.min(...cover.map((c) => c.w.y0)),
            x1: Math.max(...cover.map((c) => c.w.x1)),
            y1: Math.max(...cover.map((c) => c.w.y1)),
          },
        });
      }
    }
  }
  return hits;
}

/**
 * Einen Feldwert den Wort-Boxen zuordnen (feldtyp-bewusst).
 * @returns {Array<{page:number, box:{x0:number,y0:number,x1:number,y1:number}}>}
 */
export function matchValue(field, value, words) {
  const v = String(value ?? '').trim();
  if (!v || !words || !Array.isArray(words.pages)) return [];
  const isNum = NUMERIC_FIELDS.has(field);
  const isDate = DATE_FIELDS.has(field);
  const numTarget = isNum ? parseNum(v) : null;
  const dateCands = isDate ? dateCandidates(v) : null;
  const target = normId(v);
  const hits = [];
  for (const pg of words.pages) {
    for (const w of pg.words || []) {
      let ok = false;
      if (isNum) {
        if (numTarget != null) { const n = parseNum(w.t); ok = n != null && approxEq(n, numTarget); }
      } else if (isDate) {
        const tn = String(w.t ?? '').replace(/\s/g, '');
        ok = !!dateCands && dateCands.some((c) => tn.includes(c));
      } else {
        const wn = normId(w.t);
        if (wn && target) {
          if (field === 'waers') {
            ok = wn === target || (target === 'eur' && (wn === 'eur' || String(w.t ?? '').includes('€')));
          } else {
            // ID/Text: exakt, oder Teilstring (Laengenschutz gegen Mini-Treffer; deckt fuehrende
            // Nullen wie EBELN 45054087 <-> 0045054087 und Teil-Tokens ab).
            ok = wn === target || (target.length >= 4 && wn.length >= 3 && (wn.includes(target) || target.includes(wn)));
          }
        }
      }
      if (ok) hits.push({ page: pg.page, box: { x0: w.x0, y0: w.y0, x1: w.x1, y1: w.y1 } });
    }
  }
  if (hits.length) return hits;
  // Kein Einzelwort-Treffer -> mehrteiligen Textwert zeilenweise suchen.
  if (!isNum && !isDate && v.includes(' ')) return matchMultiWord(v, words.pages);
  return hits;
}

/**
 * Baut Fundstellen fuer alle Zeilen/Felder eines Dokuments.
 * @param {{pages: Array<{page:number, width:number, height:number, words:Array<{x0:number,y0:number,x1:number,y1:number,t:string}>}>}} words
 * @param {Array<Object>} lines Zeilen mit lineNo + Feldwerten (AbDocumentDetail: doc.lines)
 * @returns {Map<number, Map<string, Array<{page:number,x0:number,y0:number,x1:number,y1:number}>>>}
 *          lineNo -> field -> Liste der Wort-Boxen (PDF-Punkt-Koordinaten, wie von /words geliefert).
 */
export function buildHighlights(words, lines) {
  const byLine = new Map();
  if (!words || !Array.isArray(words.pages) || !Array.isArray(lines)) return byLine;
  for (const line of lines) {
    if (line?.lineNo == null) continue;
    const byField = new Map();
    for (const f of SOURCE_FIELDS) {
      const raw = line[f];
      const val = raw === null || raw === undefined ? '' : String(raw).trim();
      if (!val) continue;
      const hits = matchValue(f, val, words);
      if (!hits.length) continue;
      byField.set(f, hits.map((h) => ({ page: h.page, x0: h.box.x0, y0: h.box.y0, x1: h.box.x1, y1: h.box.y1 })));
    }
    if (byField.size) byLine.set(line.lineNo, byField);
  }
  return byLine;
}

const wordMatch = { matchValue, buildHighlights };
export default wordMatch;
